// role-based access control shared by the SPA (to gate UI) and the Worker
// (to ENFORCE server-side)
/** built-in seed roles, admins may add more at runtime, so a role is really just a string */
export const ROLES = [
    'administrator',
    'moderator',
    'sales',
    'assembly',
    'assembly_coordinator',
    'service',
    'grp',
    'quality_control',
    'engineering',
    'procurement',
    'accounting',
    'warehouse',
    'logistics',
    'viewer',
    'readonly',
];
// hidden top-tier role, may view unmasked audit IPs
// assigned only via a migration / direct DB write. never through the UI
export const SUPERADMIN_ROLE = 'superadmin';
export const isSuperadmin = (roles) => roles.includes(SUPERADMIN_ROLE);
export const ROLE_META = {
    administrator: { label: 'Admin', description: 'Full access to every section and action.', system: true },
    moderator: { label: 'Moderator', description: 'Operational management across every module.', system: true },
    sales: { label: 'Sales', description: 'Configurator orders (create / confirm / edit) and customer records.', system: true },
    assembly: { label: 'Assembly', description: 'Assembly floor: production data, assembly orders & export, inspection, scheduling.', system: true },
    assembly_coordinator: { label: 'Assembly Coordinator', description: 'Dashboard, agenda & calendar, plus the Assembly Progress view.', system: false },
    service: { label: 'Service', description: 'The Service page and the client Service Requests (view, edit, approve / decline).', system: false },
    grp: { label: 'GRP', description: 'GRP / hull inspection access.', system: true },
    quality_control: { label: 'Quality Control', description: 'Quality: GRP, PDI, inspection and reporting (view).', system: true },
    engineering: { label: 'Engineering', description: 'Technical & assembly data (view).', system: false },
    procurement: { label: 'Procurement', description: 'Purchasing: sales & reporting visibility, customers.', system: true },
    accounting: { label: 'Accounting', description: 'Orders, customers & reporting visibility; manage fueling invoices.', system: true },
    warehouse: { label: 'Warehouse', description: 'Warehouse & logistics: vessel status, schedule, board, deliveries.', system: true },
    logistics: { label: 'Logistics', description: 'Deliveries & shipping: vessel status, schedule, pre-delivery alerts, reporting.', system: true },
    viewer: { label: 'Viewer', description: 'Read-only access to permitted sections.', system: false },
    readonly: { label: 'Read Only', description: 'Legacy read-only role.', system: true },
};
/** every capability the app checks format: "<resource>:<action>" */
export const PERMISSIONS = [
    'overview:read',
    'overview:write',
    'service:read',
    'service:write',
    'servicereq:read',
    'servicereq:write',
    'production:read',
    'production:write',
    'pdi:read',
    'pdi:write',
    'ds:read',
    'ds:write',
    'grp:read',
    'grp:write',
    'report:read',
    'board:read',
    'labels:read',
    'analytics:read',
    'vesselstatus:read',
    'vesselstatus:write',
    'schedule:read',
    'schedule:write',
    'agenda:read',
    'agenda:write',
    'sales:read',
    'sales:write',
    'order:create',
    'order:confirm',
    'order:edit',
    'order:delete',
    'order:restore',
    'productionlist:export',
    'prodorders:view',
    'prodnotes:write',
    'orderlock:manage',
    'configurator:manage',
    'configurator:assembly',
    'salesorder:unlock',
    'salesorder:relock',
    'order:edit_unlocked',
    'ordernotify:receive',
    'customer_database.view',
    'customer_database.create',
    'customer_database.edit',
    'customer_database.merge',
    'customer_database.delete',
    'fueling.view',
    'fueling.edit',
    'hin:read',
    'hin:write',
    'management:view',
    'bom:manage',
    'admin:users',
    'admin:roles',
    'admin:audit',
    'admin:config',
    'admin:mappings',
    'admin:tickets',
];
export const PERMISSION_CATALOG = [
    // Configuration (the menu's section): Sales builds and confirms orders in the configurator; Assembly accepts
    // them, puts them in production and opens them for Sales to change (a confirmed order is locked for Sales)
    { id: 'order:create', module: 'Configuration · Sales', label: 'Build orders in the configurator (drafts)' },
    { id: 'order:confirm', module: 'Configuration · Sales', label: 'Confirm orders (they go to Assembly to accept)' },
    { id: 'order:edit', module: 'Configuration · Sales', label: 'Change their confirmed orders while Assembly has them open' },
    { id: 'order:edit_unlocked', module: 'Configuration · Sales', label: 'Change a confirmed order Assembly opened (that only)' },
    { id: 'order:delete', module: 'Configuration · Sales', label: 'Delete their draft orders' },
    { id: 'order:restore', module: 'Configuration · Sales', label: 'Restore deleted orders' },
    { id: 'prodorders:view', module: 'Configuration · Assembly', label: 'See Assembly Orders (every order)' },
    { id: 'orderlock:manage', module: 'Configuration · Assembly', label: 'Accept orders, put them in production, change or delete confirmed ones' },
    { id: 'salesorder:unlock', module: 'Configuration · Assembly', label: 'Open a confirmed order for Sales to change' },
    { id: 'salesorder:relock', module: 'Configuration · Assembly', label: 'Lock an opened order again' },
    { id: 'productionlist:export', module: 'Configuration · Assembly', label: 'Export the assembly list' },
    { id: 'prodnotes:write', module: 'Configuration · Assembly', label: 'Mark order requirements seen / done' },
    { id: 'configurator:manage', module: 'Configuration Setup', label: 'Catalogue, prices and order-file ties' },
    { id: 'configurator:assembly', module: 'Configuration Setup', label: 'Assembly export and helm / seating fields' },
    { id: 'sales:read', module: 'Sales', label: 'Sales pages - view (orders, order sheets, change log)' },
    { id: 'sales:write', module: 'Sales', label: 'Sales order sheets - edit' },
    { id: 'ordernotify:receive', module: 'Sales', label: 'Receive order-change notifications' },
    { id: 'customer_database.view', module: 'Customer Database', label: 'View customers' },
    { id: 'customer_database.create', module: 'Customer Database', label: 'Create customers' },
    { id: 'customer_database.edit', module: 'Customer Database', label: 'Edit customers' },
    { id: 'customer_database.merge', module: 'Customer Database', label: 'Merge duplicate customers' },
    { id: 'customer_database.delete', module: 'Customer Database', label: 'Archive / restore customers' },
    { id: 'fueling.view', module: 'Accounting', label: 'View fueling invoices' },
    { id: 'fueling.edit', module: 'Accounting', label: 'Add / edit fueling invoices' },
    { id: 'grp:read', module: 'GRP Inspection', label: 'View GRP inspection' },
    { id: 'grp:write', module: 'GRP Inspection', label: 'Edit GRP inspection' },
    { id: 'overview:read', module: 'Assembly Data', label: 'Overview - view' },
    { id: 'overview:write', module: 'Assembly Data', label: 'Overview - edit' },
    { id: 'service:read', module: 'Service', label: 'Service - view' },
    { id: 'service:write', module: 'Service', label: 'Service - edit' },
    { id: 'servicereq:read', module: 'Service', label: 'Client service requests - view' },
    { id: 'servicereq:write', module: 'Service', label: 'Client service requests - approve / decline' },
    { id: 'production:read', module: 'Assembly Data', label: 'Assembly progress - view' },
    { id: 'production:write', module: 'Assembly Data', label: 'Assembly progress - edit' },
    { id: 'pdi:read', module: 'Assembly Data', label: 'PDI - view' },
    { id: 'pdi:write', module: 'Assembly Data', label: 'PDI - edit' },
    { id: 'ds:read', module: 'Assembly Data', label: 'DS programming - view' },
    { id: 'ds:write', module: 'Assembly Data', label: 'DS programming - edit' },
    { id: 'report:read', module: 'Assembly Data', label: 'Reporting - view' },
    { id: 'board:read', module: 'Assembly Data', label: 'Assembly board - view' },
    { id: 'labels:read', module: 'Assembly Data', label: 'Labels - view' },
    { id: 'schedule:read', module: 'Planning', label: 'Schedule - view' },
    { id: 'schedule:write', module: 'Planning', label: 'Schedule - edit' },
    { id: 'agenda:read', module: 'Planning', label: 'Agenda - view' },
    { id: 'agenda:write', module: 'Planning', label: 'Agenda - edit' },
    { id: 'vesselstatus:read', module: 'Warehouse', label: 'Vessel status - view' },
    { id: 'vesselstatus:write', module: 'Warehouse', label: 'Vessel status - edit' },
    { id: 'analytics:read', module: 'Analytics', label: 'View analytics' },
    { id: 'hin:read', module: 'Vessel Identification', label: 'Vessel Identification - view' },
    { id: 'hin:write', module: 'Vessel Identification', label: 'Vessel Identification - edit HIN' },
    { id: 'management:view', module: 'Management', label: 'Access the Management section' },
    { id: 'bom:manage', module: 'Management', label: 'Manage the BOM lists (STD lists, standard equipment, option rules)' },
    { id: 'admin:users', module: 'Administration', label: 'Manage users' },
    { id: 'admin:roles', module: 'Administration', label: 'Manage roles & permissions' },
    { id: 'admin:audit', module: 'Administration', label: 'View audit logs' },
    { id: 'admin:config', module: 'Administration', label: 'Manage settings' },
    { id: 'admin:mappings', module: 'Administration', label: 'Manage Smartsheet mappings' },
    { id: 'admin:tickets', module: 'Administration', label: 'Accept or deny tickets' },
];
/** modules in display order derived from the catalogue */
export const PERMISSION_MODULES = PERMISSION_CATALOG.reduce((acc, p) => {
    if (!acc.includes(p.module))
        acc.push(p.module);
    return acc;
}, []);
const ALL = PERMISSIONS;
const READS = PERMISSIONS.filter((p) => p.endsWith(':read'));
/** default role → permissions mapping (seeds D1), least-privilege by design */
export const DEFAULT_ROLE_PERMISSIONS = {
    administrator: [...ALL],
    moderator: [
        ...READS,
        'overview:write', 'pdi:write', 'ds:write', 'vesselstatus:write', 'schedule:write', 'agenda:write',
        'sales:write', 'admin:config', 'hin:write', 'service:write', 'servicereq:read', 'servicereq:write',
        'order:create', 'order:confirm', 'order:edit', 'order:delete',
        'productionlist:export', 'prodorders:view', 'prodnotes:write', 'orderlock:manage', 'configurator:manage', 'configurator:assembly',
        'salesorder:unlock', 'salesorder:relock', 'ordernotify:receive',
        'customer_database.view', 'customer_database.create', 'customer_database.edit', 'customer_database.merge', 'customer_database.delete',
        'fueling.view', 'fueling.edit', 'management:view', 'grp:write',
    ],
    assembly: [
        'overview:read', 'overview:write', 'service:read', 'service:write', 'servicereq:read', 'servicereq:write', 'production:read', 'production:write', 'pdi:read', 'pdi:write',
        'ds:read', 'ds:write', 'grp:read', 'grp:write', 'report:read', 'board:read', 'labels:read', 'analytics:read',
        'vesselstatus:read', 'schedule:read', 'agenda:read', 'agenda:write', 'sales:read',
        'productionlist:export', 'prodorders:view', 'prodnotes:write', 'orderlock:manage', 'configurator:manage', 'configurator:assembly',
        'salesorder:unlock', 'salesorder:relock', 'ordernotify:receive', 'customer_database.view',
        // Configuration: Assembly opens the Sales side too (builds and confirms an order in the configurator)
        'order:create', 'order:confirm',
        'hin:read', 'hin:write', 'management:view',
    ],
    // (Vessel Status, the warehouse book, to look at only)
    sales: ['sales:read', 'sales:write', 'agenda:read', 'agenda:write', 'order:create', 'order:confirm', 'order:edit', 'order:edit_unlocked', 'order:delete',
        'configurator:manage', 'customer_database.view', 'customer_database.create', 'customer_database.edit', 'management:view', 'vesselstatus:read'],
    // a light coordinator view: the Main pages (Dashboard/Agenda/Calendar) plus Assembly Progress. read-only.
    assembly_coordinator: ['agenda:read', 'production:read'],
    // the Service team: the Service page plus the client Service Requests (approve / decline + client emails).
    service: ['service:read', 'service:write', 'servicereq:read', 'servicereq:write', 'agenda:read'],
    grp: ['grp:read', 'grp:write', 'agenda:read', 'agenda:write'],
    quality_control: ['grp:read', 'grp:write', 'pdi:read', 'report:read', 'overview:read', 'service:read', 'labels:read', 'agenda:read', 'agenda:write', 'hin:read'],
    engineering: ['overview:read', 'service:read', 'ds:read', 'board:read', 'schedule:read', 'report:read', 'agenda:read', 'agenda:write', 'hin:read', 'grp:read'],
    procurement: ['sales:read', 'report:read', 'overview:read', 'service:read', 'agenda:read', 'agenda:write', 'customer_database.view', 'hin:read', 'grp:read'],
    accounting: ['sales:read', 'report:read', 'overview:read', 'service:read', 'analytics:read', 'agenda:read', 'agenda:write', 'customer_database.view', 'fueling.view', 'fueling.edit', 'hin:read', 'management:view', 'production:read', 'grp:read'],
    warehouse: ['vesselstatus:read', 'vesselstatus:write', 'schedule:read', 'board:read', 'production:read', 'report:read', 'labels:read', 'grp:read', 'analytics:read', 'overview:read', 'service:read', 'agenda:read', 'agenda:write', 'hin:read'],
    logistics: ['vesselstatus:read', 'vesselstatus:write', 'schedule:read', 'board:read', 'production:read', 'report:read', 'labels:read', 'overview:read', 'service:read', 'analytics:read', 'agenda:read', 'agenda:write', 'hin:read', 'grp:read', 'management:view'],
    viewer: [...READS], // READS already includes agenda:read
    readonly: [...READS, 'agenda:write'], // read-only everywhere, but may edit the shared agenda
};
/**
 * Each department's standard access: what its role needs to open every page of its own section of the menu
 * (apps/web/src/app/nav.ts): Sales its customers, order sheets, change log and Vessel Progress; Assembly its
 * progress, tools and checklists; GRP, Service, Accounting and Warehouse theirs; the Moderator the Management
 * section. `also` names the parts of other sections it opens as well, a whole section ("Configuration") or one
 * submenu of it ("Configuration › Sales"): in Configuration, Sales opens the Sales pages, Assembly all of it.
 * A department's role always holds these: start-up steps (_rbac_v20, _rbac_v21) put back any that were taken off
 * (Admin › Roles › Restore standard access does the same by hand), and apps/web/src/app/nav.test.ts fails when
 * a page of a section needs more than its department's standard. Editing stays as each role has it.
 */
export const DEPARTMENT_STANDARD = {
    moderator: { section: 'Management', permissions: ['management:view', 'schedule:read', 'overview:read', 'board:read', 'report:read', 'production:read'] },
    assembly: {
        section: 'Assembly',
        also: ['Configuration'],
        permissions: ['production:read', 'ds:read', 'pdi:read', 'overview:read', 'labels:read', 'sales:read', 'order:create', 'configurator:manage', 'prodorders:view'],
    },
    grp: { section: 'GRP', permissions: ['grp:read'] },
    service: { section: 'Service', permissions: ['service:read', 'servicereq:read'] },
    sales: { section: 'Sales', also: ['Configuration › Sales'], permissions: ['sales:read', 'customer_database.view', 'order:create', 'configurator:manage'] },
    accounting: { section: 'Accounting', permissions: ['fueling.view'] },
    warehouse: { section: 'Warehouse', permissions: ['vesselstatus:read', 'production:read'] },
};
/** true if the granted permission set allows the required permission */
export function can(granted, required) {
    return granted.has(required);
}
/** expand a role to its default permission set (used for seeding / fallback) */
export function permissionsForRole(role) {
    return DEFAULT_ROLE_PERMISSIONS[role] ?? [];
}
export function isRole(x) {
    return ROLES.includes(x);
}
export function permissionLabel(p) {
    return PERMISSION_CATALOG.find((e) => e.id === p)?.label ?? p;
}
/** human label for a role — its metadata label, else a prettified key ("quality_control" → "Quality Control") */
export function roleLabel(role) {
    return ROLE_META[role]?.label ?? role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
//# sourceMappingURL=rbac.js.map